/**
 * O selo de publicação virou menu: `publicado v3 ▾`, listando o histórico com a opção de restaurar.
 *
 * Ocupa a mesma área do cabeçalho que o selo estático ocupava — o cabeçalho já está cheio, então
 * trocar algo inerte por algo clicável é melhor que acrescentar botão.
 *
 * Restaurar escreve no **rascunho**, não no ar: a republicação continua passando pela validação
 * server-side, e nada muda para os visitantes sem um clique consciente em Publicar. Por isso a
 * confirmação fala de sobrescrever o rascunho, e não de "voltar a versão".
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor } from '../store/editor.js';
import { getDataSource, type VersionEntry } from '../data/index.js';
import { Badge, Button, EmptyState, RAIO, SOMBRA } from './ui.js';
import { IconeChevron, IconeHistorico } from './icons.js';

function formatarData(valor: number | null): string {
  if (!valor) return 'data desconhecida';
  return new Date(valor).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function VersionMenu() {
  const id = useEditor((state) => state.id);
  const publishedVersion = useEditor((state) => state.publishedVersion);
  const restoreVersion = useEditor((state) => state.restoreVersion);

  const [aberto, setAberto] = useState(false);
  const [versoes, setVersoes] = useState<VersionEntry[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [restaurando, setRestaurando] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    if (!id) return;
    setCarregando(true);
    setErro(null);
    try {
      setVersoes(await getDataSource().listVersions(id, 20));
    } catch (caught) {
      setErro(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setCarregando(false);
    }
  }, [id]);

  // Busca só ao abrir: histórico não muda enquanto o menu está fechado.
  useEffect(() => {
    if (aberto) void carregar();
  }, [aberto, carregar]);

  // Fechar com clique fora e com Escape — menu que só fecha pelo próprio botão irrita.
  useEffect(() => {
    if (!aberto) return;

    const noClique = (evento: MouseEvent): void => {
      if (!containerRef.current?.contains(evento.target as Node)) setAberto(false);
    };
    const naTecla = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') setAberto(false);
    };

    document.addEventListener('mousedown', noClique);
    document.addEventListener('keydown', naTecla);
    return () => {
      document.removeEventListener('mousedown', noClique);
      document.removeEventListener('keydown', naTecla);
    };
  }, [aberto]);

  const restaurar = async (version: number): Promise<void> => {
    const confirmado = window.confirm(
      `Restaurar a versão ${version}?\n\n` +
        'O rascunho atual será sobrescrito por ela. O widget no ar não muda até você clicar em ' +
        'Publicar.',
    );
    if (!confirmado) return;

    setRestaurando(version);
    try {
      await restoreVersion(version);
      setAberto(false);
    } finally {
      setRestaurando(null);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setAberto((valor) => !valor)}
        aria-expanded={aberto}
        aria-haspopup="true"
        title="Histórico de publicações"
        className={`inline-flex items-center gap-1 transition hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${RAIO.pilula}`}
      >
        {/* O chevron é um glifo do conjunto, não o caractere "▾": o "▾" tem largura e altura
            diferentes em cada fonte de sistema, e desalinhava o selo em Windows. */}
        {publishedVersion !== null ? (
          <Badge tone="ok">
            publicado v{publishedVersion}
            <IconeChevron size={12} />
          </Badge>
        ) : (
          <Badge>
            nunca publicado
            <IconeChevron size={12} />
          </Badge>
        )}
      </button>

      {aberto ? (
        <div
          className={`absolute top-full left-0 z-50 mt-2 w-80 border border-slate-200 bg-white p-2 ${RAIO.card} ${SOMBRA.menu}`}
        >
          <p className="px-2 py-1 text-xs font-semibold tracking-wide text-slate-500 uppercase">
            Histórico de publicações
          </p>

          {carregando ? (
            <p className="px-2 py-3 text-sm text-slate-500">Carregando…</p>
          ) : erro ? (
            <p role="alert" className="px-2 py-3 text-sm text-red-700">
              {erro}
            </p>
          ) : versoes.length === 0 ? (
            <EmptyState
              icon={IconeHistorico}
              title="Nenhuma versão registrada"
              description="O histórico guarda cada publicação a partir de agora — publique uma vez e ela aparece aqui."
            />
          ) : (
            <ul className="max-h-72 space-y-1 overflow-auto">
              {versoes.map((entrada) => {
                const atual = entrada.version === publishedVersion;
                return (
                  <li
                    key={entrada.version}
                    className={`flex items-center justify-between gap-2 px-2 py-2 hover:bg-slate-50 ${RAIO.controle}`}
                  >
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-sm font-medium text-slate-800">
                        v{entrada.version}
                        {atual ? <Badge tone="ok">no ar</Badge> : null}
                      </p>
                      <p className="text-xs text-slate-500">
                        {entrada.nodeCount} {entrada.nodeCount === 1 ? 'bloco' : 'blocos'} ·{' '}
                        {formatarData(entrada.publishedAt)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      disabled={restaurando !== null}
                      onClick={() => void restaurar(entrada.version)}
                    >
                      {restaurando === entrada.version ? 'Restaurando…' : 'Restaurar'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="border-t border-slate-100 px-2 pt-2 text-sm leading-snug text-slate-500">
            Restaurar sobrescreve o rascunho. O widget no ar só muda ao publicar, e a numeração segue
            adiante — restaurar a v1 e publicar cria a próxima versão, não uma segunda v1.
          </p>
        </div>
      ) : null}
    </div>
  );
}
